import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, UserCog, Phone, Mail, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    schedule: '09:00 - 18:00',
    services: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [staffRes, servicesRes] = await Promise.all([
        axios.get(`${API}/api/staff`),
        axios.get(`${API}/api/services`),
      ]);
      setStaff(staffRes.data);
      setServices(servicesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setEditingStaff(member);
      setFormData({
        name: member.name,
        email: member.email || '',
        phone: member.phone || '',
        schedule: member.schedule,
        services: member.services || []
      });
    } else {
      setEditingStaff(null);
      setFormData({ name: '', email: '', phone: '', schedule: '09:00 - 18:00', services: [] });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      if (editingStaff) {
        await axios.put(`${API}/api/staff/${editingStaff.id}`, formData);
        setStaff(staff.map(s => s.id === editingStaff.id ? { ...s, ...formData } : s));
        toast.success('Personal actualizado');
      } else {
        const { data } = await axios.post(`${API}/api/staff`, formData);
        setStaff([data, ...staff]);
        toast.success('Personal agregado');
      }
      setShowModal(false);
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/staff/${deleteId}`);
      setStaff(staff.filter(s => s.id !== deleteId));
      toast.success('Personal eliminado');
    } catch (err) {
      toast.error('Error al eliminar');
    } finally {
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const toggleService = (serviceId) => {
    if (formData.services.includes(serviceId)) {
      setFormData({ ...formData, services: formData.services.filter(s => s !== serviceId) });
    } else {
      setFormData({ ...formData, services: [...formData.services, serviceId] });
    }
  };

  return (
    <Layout>
      <motion.div 
        data-testid="staff-page" 
        className="space-y-8"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-headline text-white">Personal</h1>
            <p className="text-caption mt-1">{staff.length} miembros del equipo</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            data-testid="add-staff-button"
            className="nova-btn nova-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nuevo Personal
          </button>
        </motion.div>

        {/* Staff Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-52 nova-skeleton rounded-xl" />
            ))}
          </div>
        ) : staff.length === 0 ? (
          <motion.div variants={item} className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-5">
              <UserCog className="w-8 h-8 text-indigo-400" />
            </div>
            <p className="text-sm font-medium text-white mb-1">No hay personal</p>
            <p className="text-caption">Agrega tu primer miembro del equipo</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-5 nova-btn nova-btn-secondary"
            >
              Agregar personal
            </button>
          </motion.div>
        ) : (
          <motion.div variants={container} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {staff.map((member, i) => (
              <motion.div
                key={member.id}
                variants={item}
                className="nova-card nova-card-hover p-6 group"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="nova-avatar nova-avatar-lg">
                    {member.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleOpenModal(member)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4 text-[var(--nova-text-secondary)]" />
                    </button>
                    <button
                      onClick={() => { setDeleteId(member.id); setShowDeleteDialog(true); }}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
                <h3 className="text-title text-white mb-3">{member.name}</h3>
                <div className="space-y-2 text-sm text-[var(--nova-text-tertiary)]">
                  {member.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[var(--nova-text-quaternary)]" />
                      <span className="truncate">{member.email}</span>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[var(--nova-text-quaternary)]" />
                      <span>{member.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[var(--nova-text-quaternary)]" />
                    <span>{member.schedule}</span>
                  </div>
                </div>
                {member.services?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {member.services.slice(0, 3).map((sId) => {
                      const service = services.find(s => s.id === sId);
                      return service ? (
                        <span
                          key={sId}
                          className="text-xs px-2.5 py-1 rounded-lg font-medium"
                          style={{ 
                            backgroundColor: `${service.color}15`, 
                            color: service.color 
                          }}
                        >
                          {service.name}
                        </span>
                      ) : null;
                    })}
                    {member.services.length > 3 && (
                      <span className="text-xs px-2 py-1 text-[var(--nova-text-quaternary)]">+{member.services.length - 3}</span>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Staff Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] text-[var(--nova-text)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-title">{editingStaff ? 'Editar Personal' : 'Nuevo Personal'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="staff-name-input"
                  className="nova-input"
                  placeholder="Nombre completo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="nova-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="nova-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Horario</label>
                <input
                  type="text"
                  value={formData.schedule}
                  onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                  className="nova-input"
                  placeholder="Ej: 09:00 - 18:00"
                />
              </div>
              {services.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Servicios asignados</label>
                  <div className="flex flex-wrap gap-2">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`text-sm px-3 py-1.5 rounded-xl transition-all font-medium ${
                          formData.services.includes(service.id)
                            ? 'text-white shadow-lg'
                            : 'bg-[var(--nova-surface-raised)] text-[var(--nova-text-tertiary)] border border-[var(--nova-border)] hover:border-[var(--nova-border-strong)]'
                        }`}
                        style={formData.services.includes(service.id) ? { backgroundColor: service.color, boxShadow: `0 4px 12px ${service.color}40` } : {}}
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="nova-btn nova-btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-staff-button"
                  className="nova-btn nova-btn-primary flex-1"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">¿Eliminar personal?</AlertDialogTitle>
              <AlertDialogDescription className="text-[var(--nova-text-secondary)]">
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="nova-btn nova-btn-secondary">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600 rounded-xl">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    </Layout>
  );
}
